import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/config';
import { getApiErrorMessage } from '../../lib/apiError';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';
import { describeAlert, type AlertView } from './securityNotMe.helpers';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';

type Phase = 'loading' | 'confirm' | 'working' | 'done' | 'invalid';

/**
 * Lands the "Yes, it was me" link of the unfamiliar-sign-in e-mail.
 *
 * Opening the page changes NOTHING (mail scanners and link previewers open links on their
 * own); the sign-in is marked confirmed only by the button below. No sign-in is needed: the
 * emailed token is the credential, it is stripped from the address bar before any request and
 * travels in a POST body. The "No, secure my account" link of the same e-mail goes to the
 * "this wasn't me" page.
 */
export default function NewSignIn(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [alert, setAlert] = useState<AlertView | null>(null);
  const [message, setMessage] = useState('');
  const token = useRef('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    token.current = readLinkToken();
    clearLinkFromAddressBar();
    if (!token.current) {
      setPhase('invalid');
      setMessage('This link is incomplete. Please contact support right away.');
      return;
    }
    axios
      .post<AlertView>(`${API_URL}/api/v1/security/not-me/inspect`, { token: token.current })
      .then((res) => {
        setAlert(res.data);
        if (res.data.state === 'open') {
          setPhase('confirm');
        } else if (res.data.state === 'used') {
          setPhase('done');
          setMessage('You have already answered this e-mail.');
        } else {
          setPhase('invalid');
          setMessage('This link has expired. Please contact support right away.');
        }
      })
      .catch((err: unknown) => {
        setPhase('invalid');
        setMessage(getApiErrorMessage(err, 'This link is not valid. Please contact support.'));
      });
  }, []);

  const confirm = useCallback(async () => {
    setPhase('working');
    try {
      const res = await axios.post<{ message: string }>(
        `${API_URL}/api/v1/security/new-sign-in/confirm`,
        { token: token.current },
      );
      setMessage(res.data.message);
      setPhase('done');
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err, 'We could not record your answer. Please contact support.'));
      setPhase('invalid');
    }
  }, []);

  const heading = {
    loading: 'One moment…',
    confirm: 'Was this you?',
    working: 'One moment…',
    done: 'Thank you',
    invalid: 'We could not open this link',
  }[phase];

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})` }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h1 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>
          {heading}
        </h1>

        {phase === 'confirm' && alert && (
          <>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {describeAlert(alert)}
            </p>
            <button
              type="button"
              onClick={() => void confirm()}
              className="w-full rounded-xl px-6 py-3 text-sm font-semibold mb-3"
              style={{
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                color: NAVY,
              }}
            >
              Yes, it was me
            </button>
            <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
              If it was not you, use the other button in the e-mail, or contact support right away.
            </p>
          </>
        )}

        {phase === 'invalid' && (
          <p
            className="text-sm leading-relaxed mb-6"
            role="alert"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {message}
          </p>
        )}
        {phase === 'done' && (
          <output
            className="block text-sm leading-relaxed mb-6"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {message}
          </output>
        )}

        {(phase === 'done' || phase === 'invalid') && (
          <Link
            to="/login"
            className="inline-block rounded-xl px-6 py-3 text-sm font-semibold no-underline"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, color: NAVY }}
          >
            Continue to sign in
          </Link>
        )}
      </div>
    </div>
  );
}
